import React, { useState } from 'react';
import {
    ChevronLeft, ChevronRight, Mail, Phone, MessageSquare,
    CheckCircle, Clock, Calendar, Send, XCircle, Play
} from 'lucide-react';
import CustomSelect from './CustomSelect';
import EmailPreview from './EmailPreview';
import AdvancedContactSelector from './AdvancedContactSelector';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

//const API_BASE = 'http://localhost:3001';
const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

// Convierte un objeto Date a cadena 'YYYY-MM-DDTHH:mm' en la zona horaria local del usuario
const toLocalDateTimeInput = (dateObj) => {
    const d = dateObj instanceof Date && !isNaN(dateObj.getTime()) ? dateObj : new Date(Date.now() + 30 * 60000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CreateAlert = ({
    plantillas,
    contactos,
    listas = [],
    fetchContactosDeLista,
    usuario,
    tiposAlerta,
    onVolver,
    registrarActividad,
    aprobacionRequerida,
    setAlertasPendientes,
    alertasPendientes,
    setAlertasProgramadas,
    alertasProgramadas,
    clienteActivo = null,
    clientes = []
}) => {

    // Estado del flujo de alerta
    const [flujoAlerta, setFlujoAlerta] = useState(() => {
        const guardado = sessionStorage.getItem('flujoAlertaGuardado');
        if (guardado) {
            try { return JSON.parse(guardado); } catch (e) {}
        }
        return {
            paso: 1,
            tipoAlerta: null,
            canales: { email: false, sms: false, whatsapp: false },
            plantillasSeleccionadas: { email: null, sms: null, whatsapp: null },
            contactosSeleccionados: [],
            variables: {},
            preview: null,
            emisorEmailId: null,
            emisorSms: '',        // '' = usar la configuración del cliente
            emisorWhatsapp: '',   // '' = usar la configuración del cliente
            programacion: {
                inmediata: true,
                programada: { activa: false, fechaHora: '' },
                recurrente: { activa: false, intervalo: 1, unidad: 'horas', fechaInicio: '', fechaFin: '' }
            }
        };
    });

    // Emisores de email disponibles
    const [emisores, setEmisores] = useState([]);
    const [clientesList, setClientesList] = useState([]);
    const [remitentes, setRemitentes] = useState({ numeros: [], sms_por_defecto: null, whatsapp_por_defecto: null });
    const [smsNombrePersonalizado, setSmsNombrePersonalizado] = useState(false);
    const [programadasServidor, setProgramadasServidor] = useState([]);
    const [cargandoProgramadas, setCargandoProgramadas] = useState(false);

    const cargarProgramadasServidor = React.useCallback(async () => {
        setCargandoProgramadas(true);
        try {
            const r = await fetch(`${API_BASE}/api/enviar/programadas`, { headers: getAuthorizedHeaders() });
            const d = await r.json();
            if (d.success && Array.isArray(d.data)) {
                setProgramadasServidor(d.data);
            }
        } catch (e) {
            console.error('Error cargando alertas programadas:', e);
        } finally {
            setCargandoProgramadas(false);
        }
    }, []);

    const cancelarProgramadaServidor = async (id) => {
        if (!window.confirm('¿Seguro que deseas cancelar este envío programado?')) return;
        try {
            const r = await fetch(`${API_BASE}/api/enviar/${id}/cancelar`, {
                method: 'PATCH',
                headers: getAuthorizedHeaders()
            });
            const d = await r.json();
            if (d.success) {
                cargarProgramadasServidor();
            } else {
                alert('Error: ' + (d.error || 'No se pudo cancelar'));
            }
        } catch (e) {
            alert('Error de conexión al cancelar alerta programada');
        }
    };

    const enviarAhoraProgramadaServidor = async (id) => {
        if (!window.confirm('¿Deseas adelantar este envío y enviarlo ahora mismo?')) return;
        try {
            const r = await fetch(`${API_BASE}/api/enviar/${id}/enviar-ahora`, {
                method: 'POST',
                headers: getAuthorizedHeaders()
            });
            const d = await r.json();
            if (d.success) {
                alert(d.mensaje || 'Alerta enviada correctamente');
                cargarProgramadasServidor();
            } else {
                alert('Error: ' + (d.error || 'No se pudo enviar'));
            }
        } catch (e) {
            alert('Error de conexión al enviar alerta programada');
        }
    };

    React.useEffect(() => {
        fetch(`${API_BASE}/api/emisores`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setEmisores(d.data); }).catch(() => {});
        fetch(`${API_BASE}/api/clientes`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setClientesList(d.data); }).catch(() => {});
        fetch(`${API_BASE}/api/remitentes`, { headers: getAuthorizedHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setRemitentes(d.data); }).catch(() => {});
        cargarProgramadasServidor();
    }, [cargarProgramadasServidor]);

    // Remitente efectivo según configuración del cliente (para mostrar al usuario)
    const esClienteMalagaActivo = clienteActivo?.codigo === 'MALAGA_RESP' || String(clienteActivo?.nombre || '').toLowerCase().includes('málaga responde');
    const smsClienteDefecto = esClienteMalagaActivo ? '+16185905357' : (clienteActivo?.sms_sender_id || clienteActivo?.sms_number || remitentes.sms_por_defecto || 'número del sistema');
    const waClienteDefecto = esClienteMalagaActivo ? '+16185905357' : ((clienteActivo?.whatsapp_number || '').replace('whatsapp:', '') || remitentes.whatsapp_por_defecto || 'número del sistema');
    const numerosPermitidosCliente = (remitentes.numeros || []).filter(n =>
        esClienteMalagaActivo ? n.numero === '+16185905357' : n.numero !== '+16185905357'
    );
    const esNumero = (v) => /^\+?\d{6,}$/.test(String(v || '').replace(/\s/g, ''));


    // Estado selector de listas
    const [cargandoLista, setCargandoLista] = useState(false);
    const [listasAplicadas, setListasAplicadas] = useState(() => {
        const guardado = sessionStorage.getItem('listasAplicadasGuardadas');
        if (guardado) {
            try { return JSON.parse(guardado); } catch (e) {}
        }
        return [];
    });


    // Efecto para guardar estado
    React.useEffect(() => {
        sessionStorage.setItem('flujoAlertaGuardado', JSON.stringify(flujoAlerta));
    }, [flujoAlerta]);

    React.useEffect(() => {
        sessionStorage.setItem('listasAplicadasGuardadas', JSON.stringify(listasAplicadas));
    }, [listasAplicadas]);

    const handleVolver = () => {
        sessionStorage.removeItem('flujoAlertaGuardado');
        sessionStorage.removeItem('listasAplicadasGuardadas');
        onVolver();
    };

    // Toggle canal
    const toggleCanal = (canal) => {
        setFlujoAlerta({
            ...flujoAlerta,
            canales: {
                ...flujoAlerta.canales,
                [canal]: !flujoAlerta.canales[canal]
            }
        });
    };

    // Generar preview
    const generarPreview = () => {
        // Validar programación antes de pasar al paso 6
        const esProg = Boolean(flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata);
        if (!flujoAlerta.programacion?.inmediata && !flujoAlerta.programacion?.programada?.activa) {
            alert('Selecciona "Envío inmediato" o "Programar envío" para continuar.');
            return;
        }
        if (esProg) {
            const fh = flujoAlerta.programacion?.programada?.fechaHora;
            if (!fh) {
                alert('Por favor, selecciona el día y la hora para el envío programado.');
                return;
            }
            const fechaProg = new Date(fh);
            if (isNaN(fechaProg.getTime())) {
                alert('La fecha y hora seleccionada no es válida.');
                return;
            }
            if (fechaProg.getTime() <= Date.now()) {
                alert('La fecha y hora programada debe ser posterior al momento actual.');
                return;
            }
        }

        const previews = {};
        const primerContacto = contactos.find(c => flujoAlerta.contactosSeleccionados.includes(c.id)) || contactos[0] || null;
        const autoValoresContacto = {
            nombre: primerContacto?.nombre || 'María García',
            zona: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            distrito: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            distrito_municipal: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
        };

        Object.keys(flujoAlerta.canales).forEach(canal => {
            if (flujoAlerta.canales[canal] && flujoAlerta.plantillasSeleccionadas[canal]) {
                const plantilla = plantillas.find(
                    p => p.id === flujoAlerta.plantillasSeleccionadas[canal]
                );

                if (plantilla) {
                    let mensaje = plantilla.mensaje;
                    let asunto = plantilla.asunto || '';
                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                    const detectadas = [...(mensaje + asunto).matchAll(/{{(.*?)}}/g)].map(m => m[1].trim());
                    const todasVars = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas])];

                    todasVars.forEach(vnClean => {
                        let val = flujoAlerta.variables?.[vnClean];
                        // Si la variable es numérica (ej. "1", "2"), mirar si está mapeada en varsManuales (ej. ["nombre", "zona"])
                        if (!val && !isNaN(vnClean)) {
                            const mappedName = varsManuales[Number(vnClean) - 1];
                            if (mappedName) {
                                val = flujoAlerta.variables?.[mappedName] || autoValoresContacto[mappedName];
                            }
                        }
                        if (!val && autoValoresContacto[vnClean]) {
                            val = autoValoresContacto[vnClean];
                        }
                        const valMarker = val ? `[[RESALTAR]]${val}[[FIN]]` : `{{${vnClean}}}`;
                        
                        // Reemplazo Directo (Soporta {{ var }} o {{var}})
                        const regexDirect = new RegExp(`{{\\s*${vnClean}\\s*}}`, 'g');
                        mensaje = mensaje.replace(regexDirect, valMarker);
                        asunto = asunto.replace(regexDirect, valMarker);

                        // Soporte Especial WhatsApp (nombre -> {{indice}})
                        if (canal === 'whatsapp') {
                            const idx = varsManuales.findIndex(v => v.toString().trim() === vnClean);
                            if (idx !== -1) {
                                const regexIdx = new RegExp(`{{\\s*${idx + 1}\\s*}}`, 'g');
                                mensaje = mensaje.replace(regexIdx, valMarker);
                            }
                        }
                    });

                    previews[canal] = { asunto, mensaje };
                }
            }
        });

        setFlujoAlerta({ ...flujoAlerta, preview: previews, paso: 6 });
    };

    // Enviar alertas (lógica principal: tanto envío inmediato como envío programado en el servidor)
    const enviarAlertas = async () => {
        if (flujoAlerta.contactosSeleccionados.length === 0) {
            alert('Seleccione al menos un contacto');
            return;
        }

        const esEnvioProgramado = Boolean(
            flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata
        );

        let fechaProgramadaIso = null;
        let fechaProgramadaTexto = '';

        if (esEnvioProgramado) {
            const fh = flujoAlerta.programacion?.programada?.fechaHora;
            if (!fh) {
                alert('Por favor, selecciona el día y la hora en que deseas enviar la alerta.');
                setFlujoAlerta({ ...flujoAlerta, paso: 5 });
                return;
            }
            const fechaObj = new Date(fh);
            if (isNaN(fechaObj.getTime()) || fechaObj.getTime() <= Date.now()) {
                alert('La fecha y hora programada debe ser posterior al momento actual.');
                setFlujoAlerta({ ...flujoAlerta, paso: 5 });
                return;
            }
            fechaProgramadaIso = fechaObj.toISOString();
            fechaProgramadaTexto = fechaObj.toLocaleString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            if (!window.confirm(`¿Confirmas programar el envío de esta alerta a ${flujoAlerta.contactosSeleccionados.length} destinatarios para el ${fechaProgramadaTexto}?`)) {
                return;
            }
        } else {
            if (!window.confirm(`¿Estás seguro de que deseas enviar esta alerta ahora mismo a ${flujoAlerta.contactosSeleccionados.length} destinatarios?`)) {
                return;
            }
        }

        try {
            const contactosEnvio = contactos.filter(c => flujoAlerta.contactosSeleccionados.includes(c.id));

            const response = await fetch(`${API_BASE}/api/enviar/alerta`, {
                method: 'POST',
                headers: getAuthorizedHeaders(),
                body: JSON.stringify({
                    contactos_ids: flujoAlerta.contactosSeleccionados,
                    contactos: contactosEnvio,
                    tipo_alerta: flujoAlerta.tipoAlerta,
                    canales: flujoAlerta.canales,
                    plantillas_ids: flujoAlerta.plantillasSeleccionadas,
                    variables: flujoAlerta.variables,
                    usuario_id: usuario?.id,
                    cliente_id: clienteActivo?.id || null,
                    emisor_sms: flujoAlerta.emisorSms || null,
                    emisor_whatsapp: flujoAlerta.emisorWhatsapp || null,
                    emisor_email_id: flujoAlerta.emisorEmailId || null,
                    nombre_campana: flujoAlerta.nombreCampana || null,
                    programada: esEnvioProgramado,
                    fecha_programada: fechaProgramadaIso
                })
            });

            const resultado = await response.json();

            if (resultado.success) {
                if (resultado.pending) {
                    alert(resultado.mensaje);
                    handleVolver();
                    return;
                }

                if (resultado.scheduled) {
                    registrarActividad(
                        `Programó alerta tipo ${flujoAlerta.tipoAlerta} (${contactosEnvio.length} destinatarios) para el ${fechaProgramadaTexto}`,
                        'alertas', 'info',
                        { tipo_alerta: flujoAlerta.tipoAlerta, fecha_programada: fechaProgramadaIso, alertas_ids: resultado.alertas_ids }
                    );
                    alert(`Alerta programada correctamente para el ${fechaProgramadaTexto}.\n\nEl sistema enviará automáticamente la comunicación en la fecha y hora indicadas.`);
                    handleVolver();
                    return;
                }

                registrarActividad(
                    `Envió alerta tipo ${flujoAlerta.tipoAlerta} a ${contactosEnvio.length} contactos`,
                    'alertas', 'enviar',
                    { tipo_alerta: flujoAlerta.tipoAlerta, exitosos: resultado.resultados.resumen.exitosos, fallidos: resultado.resultados.resumen.fallidos }
                );

                alert(`Envío completado:\n${resultado.resultados.resumen.exitosos} exitosos\n${resultado.resultados.resumen.fallidos} fallidos`);
                handleVolver();
            } else {
                const errorMsg = resultado.error || resultado.mensaje || 'Error desconocido del servidor';
                alert('Error al procesar alerta: ' + errorMsg);
            }

        } catch (error) {
            console.error('Error al enviar alertas:', error);
            alert('Error al enviar alertas: ' + error.message);
            registrarActividad(`Error al enviar alerta: ${error.message}`, 'alertas', 'error', { error: error.message });
        }
    };

    // Renderizar Pasos
    if (flujoAlerta.paso === 1) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={handleVolver} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Nueva Alerta</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 1 de 6: Selecciona el tipo de alerta</h3>
                    <div className="max-w-md">
                        
                        {/* Nombre de la Campana */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nombre de la Comunicación (Opcional)
                            </label>
                            <input
                                type="text"
                                value={flujoAlerta.nombreCampana || ''}
                                onChange={e => setFlujoAlerta({...flujoAlerta, nombreCampana: e.target.value})}
                                placeholder="Ej. Campaña Navidad 2026"
                                className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900 dark:text-gray-100"
                            />
                        </div>

                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipo de Alerta *</label>
                        {(() => {
                            const fallbackTipos = [
                                { codigo: 'DEMO', nombre: 'Alerta DEMO / Pruebas', categoria: 'demo' },
                                { codigo: 'GEN', nombre: 'General / Comunicado', categoria: 'general' },
                                { codigo: 'INUNDACION', nombre: 'Inundación / Lluvias', categoria: 'emergencia' },
                                { codigo: 'INCENDIO', nombre: 'Incendio / Evacuación', categoria: 'emergencia' },
                                { codigo: 'CORTE_AGUA', nombre: 'Corte de Suministro de Agua', categoria: 'servicios' },
                                { codigo: 'CORTE_LUZ', nombre: 'Corte de Suministro Eléctrico', categoria: 'servicios' },
                                { codigo: 'METEOROLOGIA', nombre: 'Alerta Meteorológica', categoria: 'emergencia' },
                                { codigo: 'TRAFICO', nombre: 'Incidencia de Tráfico', categoria: 'servicios' },
                                { codigo: 'SEGURIDAD', nombre: 'Aviso de Seguridad', categoria: 'seguridad' }
                            ];

                            const listaBase = (tiposAlerta && tiposAlerta.length > 0) ? tiposAlerta : fallbackTipos;

                            // Asegurar que DEMO y GEN siempre están presentes
                            const listaTipos = [...listaBase];
                            fallbackTipos.forEach(fb => {
                                if (!listaTipos.some(t => t.codigo === fb.codigo)) {
                                    listaTipos.push(fb);
                                }
                            });

                            const demos = listaTipos.filter(t => t.categoria === 'demo' || t.codigo === 'DEMO' || t.codigo === 'GEN' || t.codigo === 'M20');
                            const codigosDemo = demos.map(d => d.codigo);
                            const otrosTipos = listaTipos.filter(t => !codigosDemo.includes(t.codigo));

                            const otrosPlantillas = [...new Set(plantillas
                                .filter(p => p.tipo_alerta_codigo && !listaTipos.some(t => t.codigo === p.tipo_alerta_codigo))
                                .map(p => p.tipo_alerta_codigo)
                            )];

                            const options = [
                                { value: '', label: '-- Selecciona un tipo de alerta --' }
                            ];

                            if (demos.length > 0) {
                                options.push({ label: 'Alertas Demo y Pruebas', isLabel: true });
                                demos.forEach(tipo => {
                                    options.push({ value: tipo.codigo, label: `${tipo.codigo} — ${tipo.nombre || tipo.codigo}` });
                                });
                            }

                            if (otrosTipos.length > 0 || otrosPlantillas.length > 0) {
                                options.push({ label: 'Tipos de Alerta del Sistema', isLabel: true });
                                otrosTipos.forEach(tipo => {
                                    options.push({ value: tipo.codigo, label: `${tipo.codigo} — ${tipo.nombre || tipo.codigo}` });
                                });
                                otrosPlantillas.forEach(codigo => {
                                    options.push({ value: codigo, label: codigo });
                                });
                            }

                            return (
                                <CustomSelect
                                    value={flujoAlerta.tipoAlerta || ''}
                                    onChange={(v) => setFlujoAlerta({ ...flujoAlerta, tipoAlerta: v })}
                                    options={options}
                                />
                            );
                        })()}

                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => {
                                if (!flujoAlerta.tipoAlerta) return alert('Selecciona un tipo de alerta');
                                setFlujoAlerta({ ...flujoAlerta, paso: 2 });
                            }}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition"
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (flujoAlerta.paso === 2) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 1 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 2 de 6: Selecciona los canales de envío</h3>
                    <div className="space-y-3">
                        {[
                            { id: 'email', icon: Mail, label: 'Email', desc: 'Enviar por correo electrónico' },
                            { id: 'sms', icon: Phone, label: 'SMS', desc: 'Enviar por mensaje de texto' },
                            { id: 'whatsapp', icon: MessageSquare, label: 'WhatsApp', desc: 'Enviar por WhatsApp' }
                        ].map(canal => (
                            <label key={canal.id} className="flex items-center gap-3 p-4 border-2 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                                <input
                                    type="checkbox"
                                    checked={flujoAlerta.canales[canal.id]}
                                    onChange={() => toggleCanal(canal.id)}
                                    className="w-5 h-5 text-primary-600"
                                />
                                <canal.icon size={24} className="text-primary-600" />
                                <div className="flex-1">
                                    <div className="font-semibold dark:text-gray-100">{canal.label}</div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400">{canal.desc}</div>
                                </div>
                            </label>
                        ))}
                    </div>
                    <div className="mt-6">
                        <button
                            onClick={() => {
                                if (!Object.values(flujoAlerta.canales).some(v => v)) return alert('Selecciona al menos un canal');
                                setFlujoAlerta({ ...flujoAlerta, paso: 3 });
                            }}
                            className="w-full bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 3: Plantillas
    if (flujoAlerta.paso === 3) {
        const canalesActivos = Object.keys(flujoAlerta.canales).filter(c => flujoAlerta.canales[c]);
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 2 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 3 de 6: Selecciona las plantillas</h3>

                    {/* Selector de emisor de email */}
                    {flujoAlerta.canales.email && emisores.length > 0 && (
                        <div className="mb-6 p-4 border-2 border-dashed border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Emisor del email (campo "De:" que vera el receptor)
                            </label>
                            <select
                                value={flujoAlerta.emisorEmailId || ''}
                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorEmailId: e.target.value ? parseInt(e.target.value) : null })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100"
                            >
                                <option value="">Por defecto (WarnGen)</option>
                                {emisores.map(em => (
                                    <option key={em.id} value={em.id}>{em.nombre} — {em.email}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Selector de remitente SMS */}
                    {flujoAlerta.canales.sms && (() => {
                        const valorSms = flujoAlerta.emisorSms || '';
                        const modoPersonalizado = smsNombrePersonalizado || (valorSms && !esNumero(valorSms));
                        return (
                            <div className="mb-6 p-4 border-2 border-dashed border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Remitente del SMS
                                </label>
                                <select
                                    value={modoPersonalizado ? '__nombre__' : valorSms}
                                    onChange={e => {
                                        const v = e.target.value;
                                        if (v === '__nombre__') {
                                            setSmsNombrePersonalizado(true);
                                            setFlujoAlerta({ ...flujoAlerta, emisorSms: clienteActivo?.sms_sender_id || 'WarnGen' });
                                        } else {
                                            setSmsNombrePersonalizado(false);
                                            setFlujoAlerta({ ...flujoAlerta, emisorSms: v });
                                        }
                                    }}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono"
                                >
                                    <option value="">Configuración del cliente ({smsClienteDefecto})</option>
                                    {numerosPermitidosCliente.filter(n => n.sms).map(n => (
                                        <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                    ))}
                                    <option value="__nombre__">Nombre de remitente (alfanumérico)…</option>
                                </select>
                                {modoPersonalizado && (
                                    <div className="mt-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="text"
                                                maxLength={11}
                                                value={valorSms}
                                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorSms: e.target.value.slice(0, 11) })}
                                                className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono font-bold w-48"
                                                placeholder="Ej: WarnGen"
                                            />
                                            <span className="text-xs text-amber-700 dark:text-amber-300 font-mono">{valorSms.length}/11</span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            En España solo se muestra si el nombre está registrado (CNMC). Si el operador lo rechaza, se envía automáticamente desde el número del cliente.
                                        </p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Selector de remitente WhatsApp */}
                    {flujoAlerta.canales.whatsapp && (
                        <div className="mb-6 p-4 border-2 border-dashed border-green-200 dark:border-green-800 rounded-lg bg-green-50 dark:bg-green-900/20">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Remitente de WhatsApp
                            </label>
                            <select
                                value={flujoAlerta.emisorWhatsapp || ''}
                                onChange={e => setFlujoAlerta({ ...flujoAlerta, emisorWhatsapp: e.target.value })}
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 dark:text-gray-100 font-mono"
                            >
                                <option value="">Configuración del cliente ({waClienteDefecto})</option>
                                {numerosPermitidosCliente.map(n => (
                                    <option key={n.numero} value={n.numero}>{n.numero} — {n.nombre}</option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                El número debe estar dado de alta como WhatsApp Sender en Twilio.
                            </p>
                        </div>
                    )}


                    {canalesActivos.map(canal => {
                        const plantillasCanal = plantillas.filter(p => p.tipo === canal && p.tipo_alerta_codigo === flujoAlerta.tipoAlerta && p.activa !== false);
                        return (
                            <div key={canal} className="mb-6">
                                <h4 className="font-semibold text-gray-700 mb-3 capitalize">Plantilla para {canal}</h4>
                                {plantillasCanal.length === 0 ? (
                                    <p className="text-sm text-gray-500 italic">No hay plantillas activas</p>
                                ) : (
                                    <div className="space-y-2">
                                        {plantillasCanal.map(plantilla => (
                                            <label key={plantilla.id} className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition ${flujoAlerta.plantillasSeleccionadas[canal] === plantilla.id ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'}`}>
                                                <input
                                                    type="radio"
                                                    name={`plantilla-${canal}`}
                                                    checked={flujoAlerta.plantillasSeleccionadas[canal] === plantilla.id}
                                                    onChange={() => setFlujoAlerta({
                                                        ...flujoAlerta,
                                                        plantillasSeleccionadas: { ...flujoAlerta.plantillasSeleccionadas, [canal]: plantilla.id }
                                                    })}
                                                    className="mt-1"
                                                />
                                                <div className="flex-1">
                                                    <div className="font-semibold text-gray-800 dark:text-gray-100">{plantilla.nombre}</div>
                                                    {plantilla.asunto && <div className="text-sm text-gray-600 dark:text-gray-400 mt-1"><strong>Asunto:</strong> {plantilla.asunto}</div>}
                                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-2 bg-gray-50 dark:bg-gray-700 p-2 rounded border dark:border-gray-600">
                                                        <pre className="whitespace-pre-wrap font-mono text-xs">{plantilla.mensaje.substring(0, 150)}...</pre>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <div className="mt-6 flex gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 2 })} className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button
                            onClick={() => {
                                if (!canalesActivos.every(c => flujoAlerta.plantillasSeleccionadas[c])) return alert('Selecciona una plantilla para cada canal');
                                setFlujoAlerta({ ...flujoAlerta, paso: 4 });
                            }}
                            className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 4: Contactos
    if (flujoAlerta.paso === 4) {
        const contactosActivos = contactos.filter(c => c.activo);

        const toggleSelection = (ids, select) => {
            const current = new Set(flujoAlerta.contactosSeleccionados);
            ids.forEach(id => select ? current.add(id) : current.delete(id));
            setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
        };

        const selectBy = (field, value) => {
            const ids = contactosActivos.filter(c => c[field] === value).map(c => c.id);
            const allSelected = ids.every(id => flujoAlerta.contactosSeleccionados.includes(id));
            toggleSelection(ids, !allSelected);
        };

        // Cargar contactos de una lista y SUMARLOS a la selección actual
        const aplicarLista = async (lista) => {
            if (!lista || !fetchContactosDeLista) return;
            if (listasAplicadas.find(l => l.grupo_id === lista.grupo_id)) return; // ya aplicada
            setCargandoLista(true);
            try {
                const data = await fetchContactosDeLista(lista.grupo_id, 1, 2000);
                if (data.success && data.contactos?.length > 0) {
                    const ids = data.contactos.map(c => c.id);
                    const current = new Set(flujoAlerta.contactosSeleccionados);
                    ids.forEach(id => current.add(id));
                    setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
                    setListasAplicadas(prev => [...prev, { ...lista, total: data.contactos.length }]);
                }
            } catch (e) { console.error('Error aplicando lista:', e); }
            finally { setCargandoLista(false); }
        };

        const quitarLista = async (lista) => {
            // Quitar los IDs de esa lista que no estén en otras listas aplicadas
            if (!fetchContactosDeLista) return;
            setCargandoLista(true);
            try {
                const data = await fetchContactosDeLista(lista.grupo_id, 1, 2000);
                if (data.success) {
                    const idsAQuitar = new Set(data.contactos.map(c => c.id));
                    // Mantener los que pertenecen a otras listas aplicadas
                    const otraslistas = listasAplicadas.filter(l => l.grupo_id !== lista.grupo_id);
                    for (const otraLista of otraslistas) {
                        const otraData = await fetchContactosDeLista(otraLista.grupo_id, 1, 2000);
                        if (otraData.success) otraData.contactos.forEach(c => idsAQuitar.delete(c.id));
                    }
                    const current = new Set(flujoAlerta.contactosSeleccionados);
                    idsAQuitar.forEach(id => current.delete(id));
                    setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: Array.from(current) });
                }
            } catch (e) { console.error('Error quitando lista:', e); }
            finally {
                setCargandoLista(false);
                setListasAplicadas(prev => prev.filter(l => l.grupo_id !== lista.grupo_id));
            }
        };

        const listasCompletadas = listas;

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 3 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                        <ChevronLeft size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Paso 4 de 6: Selecciona los destinatarios</h3>

                    <AdvancedContactSelector 
                        contactosActivos={contactosActivos}
                        listasCompletadas={listasCompletadas}
                        fetchContactosDeLista={fetchContactosDeLista}
                        contactosSeleccionados={flujoAlerta.contactosSeleccionados}
                        setContactosSeleccionados={(ids) => setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: ids })}
                    />

                    <div className="mt-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Total seleccionado para el envío: <span className="text-primary-600 dark:text-primary-400 text-lg">{flujoAlerta.contactosSeleccionados.length}</span> destinatarios
                        </span>
                        <button
                            onClick={() => setFlujoAlerta({ ...flujoAlerta, contactosSeleccionados: [] })}
                            className="text-gray-500 hover:text-red-500 text-sm font-semibold hover:underline"
                            disabled={flujoAlerta.contactosSeleccionados.length === 0}
                        >
                            Vaciar selección
                        </button>
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 3 })} className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-100 px-6 py-3 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition font-semibold">Atrás</button>
                        <button
                            onClick={() => {
                                if (flujoAlerta.contactosSeleccionados.length === 0) return alert('Selecciona al menos un contacto');
                                setFlujoAlerta({ ...flujoAlerta, paso: 5 });
                            }}
                            className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2"
                        >
                            Continuar <ChevronRight size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 5: Programación
    if (flujoAlerta.paso === 5) {
        const esModoProgramado = Boolean(flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata);
        const rawFechaHora = flujoAlerta.programacion?.programada?.fechaHora || '';
        const [parteFecha, parteHora] = rawFechaHora.includes('T') ? rawFechaHora.split('T') : ['', ''];

        const setFechaHoraProgramada = (nuevaFechaHora) => {
            setFlujoAlerta({
                ...flujoAlerta,
                programacion: {
                    ...flujoAlerta.programacion,
                    inmediata: false,
                    programada: {
                        ...flujoAlerta.programacion.programada,
                        activa: true,
                        fechaHora: nuevaFechaHora
                    }
                }
            });
        };

        const activarModoInmediato = () => {
            setFlujoAlerta({
                ...flujoAlerta,
                programacion: {
                    ...flujoAlerta.programacion,
                    inmediata: true,
                    programada: {
                        ...flujoAlerta.programacion.programada,
                        activa: false
                    }
                }
            });
        };

        const activarModoProgramado = () => {
            const valorInicial = rawFechaHora && new Date(rawFechaHora).getTime() > Date.now()
                ? rawFechaHora
                : toLocalDateTimeInput(new Date(Date.now() + 30 * 60000));
            setFechaHoraProgramada(valorInicial);
        };

        const aplicarAtajoTiempo = (dateTarget) => {
            setFechaHoraProgramada(toLocalDateTimeInput(dateTarget));
        };

        const fechaSeleccionadaObj = rawFechaHora ? new Date(rawFechaHora) : null;
        const fechaEsValida = fechaSeleccionadaObj && !isNaN(fechaSeleccionadaObj.getTime());
        const diffMs = fechaEsValida ? fechaSeleccionadaObj.getTime() - Date.now() : -1;
        const fechaEsFutura = diffMs > 0;

        let textoCuentaAtras = '';
        if (fechaEsFutura) {
            const totalMin = Math.ceil(diffMs / 60000);
            const dias = Math.floor(totalMin / (60 * 24));
            const horas = Math.floor((totalMin % (60 * 24)) / 60);
            const mins = totalMin % 60;
            const partes = [];
            if (dias > 0) partes.push(`${dias} ${dias === 1 ? 'día' : 'días'}`);
            if (horas > 0) partes.push(`${horas} ${horas === 1 ? 'hora' : 'horas'}`);
            if (mins > 0 || partes.length === 0) partes.push(`${mins} min`);
            textoCuentaAtras = `Se enviará automáticamente dentro de ${partes.join(', ')}`;
        }

        const minDateStr = toLocalDateTimeInput(new Date()).split('T')[0];

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 4 })} className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"><ChevronLeft size={24} /></button>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-1">Paso 5 de 6: Programación de envío</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Elige si deseas enviar la alerta inmediatamente o dejarla programada para un día y hora concretos.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Opción 1: Envío inmediato */}
                        <div
                            onClick={activarModoInmediato}
                            className={`flex items-start gap-3.5 p-4 border-2 rounded-xl cursor-pointer transition ${
                                !esModoProgramado
                                    ? 'border-primary-600 bg-primary-50/70 dark:bg-primary-900/20 shadow-xs'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                        >
                            <input
                                type="radio"
                                name="modo-programacion"
                                checked={!esModoProgramado}
                                onChange={activarModoInmediato}
                                className="w-5 h-5 mt-0.5 text-primary-600"
                            />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Send size={17} className="text-primary-600" />
                                    Envío inmediato
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    La alerta se enviará al instante en cuanto confirmes en el siguiente paso.
                                </div>
                            </div>
                        </div>

                        {/* Opción 2: Programar fecha y hora */}
                        <div
                            onClick={activarModoProgramado}
                            className={`flex items-start gap-3.5 p-4 border-2 rounded-xl cursor-pointer transition ${
                                esModoProgramado
                                    ? 'border-primary-600 bg-primary-50/70 dark:bg-primary-900/20 shadow-xs'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                            }`}
                        >
                            <input
                                type="radio"
                                name="modo-programacion"
                                checked={esModoProgramado}
                                onChange={activarModoProgramado}
                                className="w-5 h-5 mt-0.5 text-primary-600"
                            />
                            <div className="flex-1">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Clock size={17} className="text-primary-600" />
                                    Programar día y hora de envío
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    Establece una fecha y hora exactas; el servidor la enviará automáticamente.
                                </div>
                            </div>
                        </div>
                    </div>

                    {esModoProgramado && (
                        <div className="p-5 bg-blue-50/60 dark:bg-gray-700/60 rounded-xl border border-blue-200 dark:border-gray-600 space-y-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                    <Calendar size={16} className="text-primary-600" />
                                    Selecciona el día y la hora exacta del envío
                                </h4>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">Atajos rápidos:</span>
                                    <button
                                        type="button"
                                        onClick={() => aplicarAtajoTiempo(new Date(Date.now() + 15 * 60000))}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-primary-500 text-gray-700 dark:text-gray-200 font-medium transition"
                                    >
                                        +15 min
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => aplicarAtajoTiempo(new Date(Date.now() + 60 * 60000))}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-primary-500 text-gray-700 dark:text-gray-200 font-medium transition"
                                    >
                                        +1 hora
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const manana9 = new Date();
                                            manana9.setDate(manana9.getDate() + 1);
                                            manana9.setHours(9, 0, 0, 0);
                                            aplicarAtajoTiempo(manana9);
                                        }}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-primary-500 text-gray-700 dark:text-gray-200 font-medium transition"
                                    >
                                        Mañana 09:00
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const manana12 = new Date();
                                            manana12.setDate(manana12.getDate() + 1);
                                            manana12.setHours(12, 0, 0, 0);
                                            aplicarAtajoTiempo(manana12);
                                        }}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-primary-500 text-gray-700 dark:text-gray-200 font-medium transition"
                                    >
                                        Mañana 12:00
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Día del envío *
                                    </label>
                                    <input
                                        type="date"
                                        min={minDateStr}
                                        value={parteFecha || ''}
                                        onChange={(e) => {
                                            const nuevaFecha = e.target.value;
                                            const horaActual = parteHora || '09:00';
                                            if (nuevaFecha) {
                                                setFechaHoraProgramada(`${nuevaFecha}T${horaActual}`);
                                            }
                                        }}
                                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1.5">
                                        Hora exacta (24h) *
                                    </label>
                                    <input
                                        type="time"
                                        value={parteHora || ''}
                                        onChange={(e) => {
                                            const nuevaHora = e.target.value;
                                            const fechaActual = parteFecha || minDateStr;
                                            if (nuevaHora) {
                                                setFechaHoraProgramada(`${fechaActual}T${nuevaHora}`);
                                            }
                                        }}
                                        className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                                    />
                                </div>
                            </div>

                            {fechaEsValida && (
                                <div className={`p-3.5 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
                                    fechaEsFutura
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200'
                                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                                }`}>
                                    <div className="text-sm font-medium">
                                        {fechaEsFutura ? (
                                            <>
                                                Programado para el{' '}
                                                <strong className="font-bold">
                                                    {fechaSeleccionadaObj.toLocaleString('es-ES', {
                                                        weekday: 'long',
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </strong>
                                            </>
                                        ) : (
                                            <span>La fecha y hora seleccionada ya ha pasado. Elige una hora futura.</span>
                                        )}
                                    </div>
                                    {fechaEsFutura && (
                                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-800/50 text-emerald-900 dark:text-emerald-100 shrink-0">
                                            {textoCuentaAtras}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Listado de alertas programadas activas en el servidor */}
                    {programadasServidor.length > 0 && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-gray-50/70 dark:bg-gray-900/40">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300 flex items-center gap-2">
                                    <Clock size={14} className="text-blue-600" />
                                    Envíos programados en cola ({programadasServidor.length})
                                </h4>
                                <button
                                    type="button"
                                    onClick={cargarProgramadasServidor}
                                    disabled={cargandoProgramadas}
                                    className="text-xs text-primary-600 hover:underline font-medium"
                                >
                                    {cargandoProgramadas ? 'Actualizando...' : 'Actualizar'}
                                </button>
                            </div>
                            <div className="space-y-2 max-h-52 overflow-y-auto">
                                {programadasServidor.map(ap => {
                                    const fechaProg = ap.fecha_programada_ms
                                        ? new Date(Number(ap.fecha_programada_ms))
                                        : new Date(ap.fecha_programada);
                                    return (
                                        <div key={ap.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                                            <div>
                                                <div className="font-bold text-gray-800 dark:text-gray-100">
                                                    #{ap.id} — {ap.nombre_campana || ap.plantilla_nombre} <span className="uppercase px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 ml-1">{ap.canal}</span>
                                                </div>
                                                <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                                                    Envío previsto: <strong>{fechaProg.toLocaleString('es-ES')}</strong> · {ap.total_destinatarios} destinatarios
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => enviarAhoraProgramadaServidor(ap.id)}
                                                    className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1"
                                                    title="Enviar ahora mismo"
                                                >
                                                    <Play size={12} /> Enviar ahora
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => cancelarProgramadaServidor(ap.id)}
                                                    className="px-2.5 py-1 rounded bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-semibold flex items-center gap-1"
                                                    title="Cancelar envío programado"
                                                >
                                                    <XCircle size={12} /> Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 4 })} className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button onClick={generarPreview} className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition font-semibold flex items-center justify-center gap-2">Continuar a Vista Previa</button>
                    </div>
                </div>
            </div>
        );
    }

    // Paso 6: Preview (Sustituye al antiguo paso 7)
    if (flujoAlerta.paso === 6) {
        const canalesActivos = Object.keys(flujoAlerta.canales).filter(c => flujoAlerta.canales[c]);
        
        // Obtener variables necesarias escaneando tanto los metadatos como el texto del mensaje
        const varsNecesarias = new Set();
        canalesActivos.forEach(canal => {
            const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
            const plantilla = plantillas.find(p => p.id === plantillaId);
            if (plantilla) {
                // 1. Añadir variables desde metadatos
                const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                varsManuales.forEach(v => varsNecesarias.add(v.toString()));

                // 2. Escanear texto en busca de {{variable}} o {{num}}
                const matches = (plantilla.mensaje + (plantilla.asunto || '')).matchAll(/{{(.*?)}}/g);
                for (const match of matches) {
                    varsNecesarias.add(match[1].trim());
                }
            }
        });

        const renderTextoConResaltado = (texto) => {
            if (!texto) return null;
            const partes = texto.split(/(\[\[RESALTAR\].*?\[\[FIN\]\])/g);
            return partes.map((parte, i) => {
                if (parte.startsWith('[[RESALTAR]]') && parte.endsWith('[[FIN]]')) {
                    const contenido = parte.replace('[[RESALTAR]]', '').replace('[[FIN]]', '');
                    return (
                        <span key={i} className="bg-blue-100 text-blue-800 px-1 rounded border border-blue-200 font-bold animate-pulse">
                            {contenido}
                        </span>
                    );
                }
                return parte;
            });
        };

        const handleVarChange = (varName, value) => {
            const nuevasVars = { ...flujoAlerta.variables, [varName]: value };
            const primerContacto = contactos.find(c => flujoAlerta.contactosSeleccionados.includes(c.id)) || contactos[0] || null;
            const autoValoresContacto = {
                nombre: primerContacto?.nombre || 'María García',
                zona: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
                distrito: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
                distrito_municipal: primerContacto?.zona || primerContacto?.distrito_municipal || 'Distrito 1 — Centro',
            };
            
            // Actualizar preview en tiempo real
            const nuevosPreviews = {};
            canalesActivos.forEach(canal => {
                const plantilla = plantillas.find(p => p.id === flujoAlerta.plantillasSeleccionadas[canal]);
                if (plantilla) {
                    let mensaje = plantilla.mensaje;
                    let asunto = plantilla.asunto || '';
                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                    const detectadas = [...(mensaje + asunto).matchAll(/{{(.*?)}}/g)].map(m => m[1].trim());
                    const todasVars = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas, ...Object.keys(nuevasVars)])];
                    
                    todasVars.forEach(vn => {
                        const vnClean = vn.toString().trim();
                        let val = nuevasVars[vnClean];
                        if (!val && !isNaN(vnClean)) {
                            const mappedName = varsManuales[Number(vnClean) - 1];
                            if (mappedName) {
                                val = nuevasVars[mappedName] || autoValoresContacto[mappedName];
                            }
                        }
                        if (!val && autoValoresContacto[vnClean]) {
                            val = autoValoresContacto[vnClean];
                        }
                        const valMarker = val ? `[[RESALTAR]]${val}[[FIN]]` : `{{${vnClean}}}`;
                        
                        // Reemplazo Directo (Soporta espacios opcionales ej: {{ var }} o {{var}})
                        const regexDirect = new RegExp(`{{\\s*${vnClean}\\s*}}`, 'g');
                        mensaje = mensaje.replace(regexDirect, valMarker);
                        asunto = asunto.replace(regexDirect, valMarker);

                        // Soporte Especial WhatsApp (nombre -> {{indice}})
                        if (canal === 'whatsapp') {
                            const idx = varsManuales.findIndex(v => v.toString().trim() === vnClean);
                            if (idx !== -1) {
                                const regexIdx = new RegExp(`{{\\s*${idx + 1}\\s*}}`, 'g');
                                mensaje = mensaje.replace(regexIdx, valMarker);
                            }
                        }
                    });

                    nuevosPreviews[canal] = { asunto, mensaje };
                }
            });

            setFlujoAlerta({ ...flujoAlerta, variables: nuevasVars, preview: nuevosPreviews });
        };

        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 5 })} className="text-gray-600 hover:text-gray-800"><ChevronLeft size={24} /></button>
                    <h2 className="text-2xl font-bold text-gray-800">Crear Alerta {flujoAlerta.tipoAlerta}</h2>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Paso 6 de 6: Vista Previa y Datos Dinámicos</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1 space-y-4">
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <h4 className="font-bold text-sm text-gray-700 uppercase mb-3 text-center border-b pb-2">Datos Dinámicos</h4>
                                {canalesActivos.map(canal => {
                                    const plantillaId = flujoAlerta.plantillasSeleccionadas[canal];
                                    const plantilla = plantillas.find(p => p.id === plantillaId);
                                    if (!plantilla) return null;

                                    const varsManuales = Array.isArray(plantilla.variables) ? plantilla.variables : JSON.parse(plantilla.variables || '[]');
                                    const detectadas = [...(plantilla.mensaje + (plantilla.asunto || '')).matchAll(/{{(.*?)}}/g)]
                                        .map(m => m[1].trim())
                                        .filter(d => !(canal === 'whatsapp' && !isNaN(d) && varsManuales[Number(d) - 1]));
                                    const varsDeCanal = [...new Set([...varsManuales.map(v => v.toString().trim()), ...detectadas])];

                                    if (varsDeCanal.length === 0) return null;

                                    return (
                                        <div key={canal} className="mb-6 p-3 bg-white rounded border border-gray-200 shadow-sm transition-all hover:border-primary-300">
                                            <h5 className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest border-b pb-1">
                                                {canal === 'email' && <Mail size={12} />}
                                                {canal === 'sms' && <Phone size={12} />}
                                                {canal === 'whatsapp' && <MessageSquare size={12} />}
                                                Variables {canal}
                                            </h5>
                                            <div className="space-y-3">
                                                {varsDeCanal.map(v => {
                                                    const idx = canal === 'whatsapp' ? varsManuales.findIndex(vm => vm.toString().trim() === v.toString().trim()) : -1;
                                                    const esAutoContacto = ['nombre', 'zona', 'distrito', 'distrito_municipal'].includes(v.toLowerCase());
                                                    return (
                                                        <div key={v}>
                                                            <div className="flex items-center justify-between gap-1 mb-1">
                                                                <label className="block text-[10px] font-bold text-primary-600 uppercase bg-primary-50 px-1 inline-block rounded">
                                                                    {v} {idx !== -1 && <span className="text-[9px] opacity-60">(&#123;&#123;{idx + 1}&#125;&#125;)</span>}
                                                                </label>
                                                                {esAutoContacto && (
                                                                    <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded font-medium">
                                                                        Automático por contacto
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={flujoAlerta.variables[v] || ''}
                                                                onChange={(e) => handleVarChange(v, e.target.value)}
                                                                className="w-full px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-primary-500 text-xs outline-none bg-gray-50 focus:bg-white transition-colors"
                                                                placeholder={esAutoContacto ? `Automático (se usa ${v} de cada contacto)` : `Valor para {{${v}}}`}
                                                            />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}

                                {canalesActivos.every(canal => {
                                    const p = plantillas.find(p => p.id === flujoAlerta.plantillasSeleccionadas[canal]);
                                    if (!p) return true;
                                    const vm = Array.isArray(p.variables) ? p.variables : JSON.parse(p.variables || '[]');
                                    const dt = [...(p.mensaje + (p.asunto || '')).matchAll(/{{(.*?)}}/g)];
                                    return vm.length === 0 && dt.length === 0;
                                }) && (
                                    <p className="text-xs text-gray-500 italic text-center py-4 bg-gray-50 rounded border border-dashed">
                                        No se han detectado variables en las plantillas seleccionadas.
                                    </p>
                                )}
                            </div>

                            <div className="bg-primary-50 p-4 rounded-lg border border-primary-100 italic">
                                <h4 className="font-semibold text-sm mb-1 text-primary-800 underline">Resumen de Envío</h4>
                                <div className="text-xs space-y-1 text-primary-700">
                                    <p>Canales: {canalesActivos.join(', ')}</p>
                                    <p>Destinatarios: {flujoAlerta.contactosSeleccionados.length}</p>
                                    <p>
                                        Programación:{' '}
                                        {flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata && flujoAlerta.programacion?.programada?.fechaHora
                                            ? `Programado para el ${new Date(flujoAlerta.programacion.programada.fechaHora).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}`
                                            : 'Envío inmediato'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="md:col-span-2 space-y-4">
                            {canalesActivos.map(canal => (
                                <div key={canal} className="border-2 border-gray-200 rounded-lg overflow-hidden relative shadow-sm">
                                    <div className="bg-gray-100 px-4 py-2 border-b flex items-center justify-between">
                                        <h5 className="font-bold text-xs uppercase text-gray-600 flex items-center gap-2">
                                            {canal === 'email' && <Mail size={14} />}
                                            {canal === 'sms' && <Phone size={14} />}
                                            {canal === 'whatsapp' && <MessageSquare size={14} />}
                                            VISTA PREVIA {canal}
                                        </h5>
                                        <span className="text-[10px] text-gray-400">Mensaje Final</span>
                                    </div>
                                    {canal === 'email' ? (
                                        <div className="p-3 bg-gray-50">
                                            <EmailPreview
                                                mensaje={flujoAlerta.preview[canal]?.mensaje || ''}
                                                asunto={
                                                    (flujoAlerta.preview[canal]?.asunto || '')
                                                        .replace(/\[\[RESALTAR\]\](.*?)\[\[FIN\]\]/g, '$1')
                                                }
                                                altura="h-[460px]"
                                                mostrarToggle={true}
                                            />
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-white min-h-[100px] max-h-[300px] overflow-y-auto">
                                            <div className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                                                {renderTextoConResaltado(flujoAlerta.preview[canal]?.mensaje) || <span className="text-gray-300 italic">Previsualización del mensaje...</span>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t">
                        <button onClick={() => setFlujoAlerta({ ...flujoAlerta, paso: 5 })} className="w-full sm:w-auto px-8 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition font-semibold">Atrás</button>
                        <button 
                            onClick={enviarAlertas}
                            className={`flex-1 text-white px-8 py-3.5 rounded-lg transition font-bold flex items-center justify-center gap-2 shadow-lg hover:-translate-y-0.5 ${
                                flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata
                                    ? 'bg-blue-600 hover:bg-blue-700'
                                    : 'bg-green-600 hover:bg-green-700'
                            }`}
                        >
                            {flujoAlerta.programacion?.programada?.activa && !flujoAlerta.programacion?.inmediata ? (
                                <>
                                    <Clock size={22} />
                                    PROGRAMAR ENVÍO ({flujoAlerta.programacion?.programada?.fechaHora
                                        ? new Date(flujoAlerta.programacion.programada.fechaHora).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                        : ''})
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={24} />
                                    ENVIAR ALERTA AHORA
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default CreateAlert;
